import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { tabLabelStyle, terminalTabShellStyle } from "@/components/common/tab-shell";
import {
	TerminalSessionView,
	type TerminalSessionPhase,
} from "@/components/terminal/TerminalSessionView";
import {
	activateTerminalTab,
	addTerminalTab,
	canAddTerminalTab,
	closeTerminalTab,
	createTerminalTab,
	restoreTerminalTabsState,
	type TerminalTab,
	type TerminalTabsState,
} from "@/lib/terminal-tabs";
import {
	clearTerminalTabs,
	readTerminalTabs,
	rewriteTerminalTabsChatKey,
	writeTerminalTabList,
} from "@/lib/terminal-session-state";
import {
	canPersistTerminalTabs,
	shouldDiscardStoredTerminalState,
	terminalContextTransition,
	type TerminalContextIdentity,
	type TerminalPersistIdentity,
} from "@/lib/terminal";
import { selectEffectiveEnvironmentId, useChatStore } from "@/stores/useChatStore";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { selectTerminalVisible, useLayoutStore } from "@/stores/useLayoutStore";

/** タブ状態と、それを読み込んだ identity を組にして stale write を防ぐ。 */
interface HydratedTerminalTabs {
	identity: TerminalPersistIdentity;
	state: TerminalTabsState;
}

function createTabsState(
	userId: string,
	environmentId: string | null,
	chatId: string | null,
): HydratedTerminalTabs {
	return {
		identity: { userId, environmentId, chatId },
		state: restoreTerminalTabsState(readTerminalTabs(userId, environmentId, chatId)),
	};
}

interface TerminalTabButtonProps {
	tab: TerminalTab;
	index: number;
	active: boolean;
	running: boolean;
	onActivate: (terminalId: string) => void;
	onClose: (terminalId: string) => void;
}

function TerminalTabButton({
	tab,
	index,
	active,
	running,
	onActivate,
	onClose,
}: TerminalTabButtonProps) {
	return (
		<div
			data-testid={`terminal-tab-${tab.terminalId}`}
			data-active={active ? "true" : "false"}
			onClick={() => onActivate(tab.terminalId)}
			onAuxClick={(event) => {
				if (event.button === 1) onClose(tab.terminalId);
			}}
			style={{
				...terminalTabShellStyle(active),
				gap: 4,
				padding: "0 8px 0 14px",
				// pane-header が引く下線の上にタブを 1px 重ね、アクティブなタブの白い下線で
				// その線を途切れさせて中身と繋げる。
				marginBottom: -1,
			}}
		>
			<span style={tabLabelStyle}>{`ターミナル ${index + 1}`}</span>
			<button
				type="button"
				data-tooltip={running ? "コマンドを実行中" : "閉じる"}
				data-testid={`terminal-tab-${tab.terminalId}-close`}
				onClick={(event) => {
					event.stopPropagation();
					onClose(tab.terminalId);
				}}
				style={{
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					width: 16,
					height: 16,
					padding: 0,
					border: "none",
					borderRadius: "var(--radius-2)",
					background: "transparent",
					color: "inherit",
					cursor: "pointer",
					flexShrink: 0,
				}}
			>
				{running ? (
					<span
						style={{
							width: 7,
							height: 7,
							borderRadius: "var(--radius-full)",
							background: "var(--fg-1)",
							display: "inline-block",
						}}
					/>
				) : (
					"×"
				)}
			</button>
		</div>
	);
}

export function TerminalPanel() {
	const currentUserId = useCurrentUserStore((state) => state.id);
	const terminalVisible = useLayoutStore(selectTerminalVisible);
	const chatId = useChatStore((state) => state.chatId);
	const environmentId = useChatStore(selectEffectiveEnvironmentId);
	const newChatEnvironmentId = useChatStore((state) => state.newChatEnvironmentId);
	const [tabsState, setTabsState] = useState<HydratedTerminalTabs>(() =>
		createTabsState(currentUserId, environmentId, chatId),
	);
	const [phaseByTerminalId, setPhaseByTerminalId] = useState<Record<string, TerminalSessionPhase>>(
		{},
	);
	const contextIdentityRef = useRef<TerminalContextIdentity>({
		chatId,
		environmentId,
		newChatEnvironmentId,
	});

	const persistTabs = useCallback(
		(next: HydratedTerminalTabs) => {
			if (environmentId === null) return; // ゲート内の判定と同値。型の絞り込みのため。
			if (
				!canPersistTerminalTabs(next.identity, { userId: currentUserId, environmentId, chatId })
			) {
				return;
			}
			writeTerminalTabList(currentUserId, environmentId, chatId, next.state);
		},
		[chatId, currentUserId, environmentId],
	);

	useEffect(() => {
		persistTabs(tabsState);
	}, [persistTabs, tabsState]);

	const canAddTab = canAddTerminalTab(tabsState.state);

	const activateTab = useCallback(
		(terminalId: string) => {
			const next = activateTerminalTab(tabsState.state, terminalId);
			if (next === tabsState.state) return;
			const hydrated = { identity: tabsState.identity, state: next };
			setTabsState(hydrated);
			persistTabs(hydrated);
		},
		[persistTabs, tabsState],
	);

	const addTab = useCallback(() => {
		if (!canAddTab) return;
		const hydrated = { identity: tabsState.identity, state: addTerminalTab(tabsState.state) };
		setTabsState(hydrated);
		persistTabs(hydrated);
	}, [canAddTab, persistTabs, tabsState]);

	const closeTab = useCallback(
		(terminalId: string) => {
			const next = closeTerminalTab(tabsState.state, terminalId, createTerminalTab);
			if (next === tabsState.state) return;
			const hydrated = { identity: tabsState.identity, state: next };
			setTabsState(hydrated);
			setPhaseByTerminalId((previous) => {
				const allowedIds = new Set(next.tabs.map((tab) => tab.terminalId));
				return Object.fromEntries(Object.entries(previous).filter(([id]) => allowedIds.has(id)));
			});
			persistTabs(hydrated);
		},
		[persistTabs, tabsState],
	);

	const handlePhaseChange = useCallback((terminalId: string, phase: TerminalSessionPhase) => {
		setPhaseByTerminalId((previous) =>
			previous[terminalId] === phase ? previous : { ...previous, [terminalId]: phase },
		);
	}, []);

	useLayoutEffect(() => {
		const previousContext = contextIdentityRef.current;
		const nextContext = { chatId, environmentId, newChatEnvironmentId };
		const transition = terminalContextTransition(previousContext, nextContext);
		contextIdentityRef.current = nextContext;
		if (transition === "unchanged") return;

		if (transition === "draft-promoted") {
			if (currentUserId !== "" && environmentId !== null && chatId !== null) {
				rewriteTerminalTabsChatKey(currentUserId, environmentId, previousContext.chatId, chatId);
				setTabsState((previous) => ({
					...previous,
					identity: { ...previous.identity, chatId },
				}));
			}
			return;
		}

		if (shouldDiscardStoredTerminalState(previousContext, environmentId)) clearTerminalTabs();
		setTabsState(createTabsState(currentUserId, environmentId, chatId));
		setPhaseByTerminalId({});
	}, [chatId, currentUserId, environmentId, newChatEnvironmentId]);

	return (
		<section
			data-testid="terminal-panel"
			style={{
				height: "100%",
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				background: "var(--surface-1)",
			}}
		>
			<header className="pane-header" style={{ minHeight: 36, padding: "0 12px" }}>
				<div
					data-testid="terminal-tabs"
					style={{ display: "flex", alignSelf: "stretch", minWidth: 0, flex: 1, overflowX: "auto" }}
				>
					{tabsState.state.tabs.map((tab, index) => (
						<TerminalTabButton
							key={tab.terminalId}
							tab={tab}
							index={index}
							active={tab.terminalId === tabsState.state.activeTerminalId}
							running={phaseByTerminalId[tab.terminalId] === "running"}
							onActivate={activateTab}
							onClose={closeTab}
						/>
					))}
					<button
						type="button"
						data-testid="terminal-tab-add"
						disabled={!canAddTab}
						data-tooltip={!canAddTab ? "ターミナルは最大5個までです" : "ターミナルを追加"}
						onClick={addTab}
						style={{
							alignSelf: "stretch",
							border: "none",
							borderBottom: "1px solid var(--line-1)",
							background: "transparent",
							padding: "0 10px",
							fontSize: "var(--text-title)",
							color: canAddTab ? "var(--fg-2)" : "var(--fg-4)",
							cursor: canAddTab ? "pointer" : "default",
							flexShrink: 0,
						}}
					>
						+
					</button>
				</div>
			</header>
			<div style={{ display: "flex", flex: 1, minHeight: 0 }}>
				{tabsState.state.tabs.map((tab) => (
					<TerminalSessionView
						key={tab.terminalId}
						terminalId={tab.terminalId}
						active={tab.terminalId === tabsState.state.activeTerminalId}
						visible={terminalVisible}
						chatId={chatId}
						environmentId={environmentId}
						refreshFilesOnExit={false}
						onPhaseChange={handlePhaseChange}
					/>
				))}
			</div>
		</section>
	);
}
